/**
 * React hook that registers a session-restore contributor and automatically
 * restores its saved value when a snapshot is available.
 */

import { useEffect, useRef } from "react";
import { sessionRestoreService, type RestoreContext, type SessionStateContributor } from "../services/sessionRestore/SessionRestoreService";
import { appSessionRestoreSlice, useEphemeralSlice } from "../store/ephemeral";

/**
 * Registers a contributor whose value is captured on save and restored when
 * `appSessionRestore.pendingSnapshot` contains the matching key.
 *
 * @typeParam TValue Type of the captured / restored value.
 * @param key - Unique key in the snapshot.
 * @param capture - Synchronous function returning the value to save, or `null`.
 * @param restore - Function applying the saved value back to the UI.
 * @param options - Optional `priority` for restore order (lower first).
 */
export function useSessionRestoreContributor<TValue>(
    key: string,
    capture: () => TValue | null,
    restore: ( value: TValue, context: RestoreContext ) => void,
    options?: { "priority"?: number },
): void {
    const priority = options?.priority ?? 0;
    const captureRef = useRef( capture );
    const restoreRef = useRef( restore );
    const hasRestoredRef = useRef( false );
    const [ appSessionRestore ] = useEphemeralSlice( appSessionRestoreSlice );

    useEffect( () => {
        captureRef.current = capture;
        restoreRef.current = restore;
    }, [ capture, restore ] );

    useEffect( () => {
        const contributor: SessionStateContributor<TValue> = {
            key,
            priority,
            "capture": () => captureRef.current(),
            "restore": ( value, context ) => {
                restoreRef.current( value, context );
            },
        };

        return sessionRestoreService.register( contributor );
    }, [ key, priority ] );

    useEffect( () => {
        if ( !appSessionRestore.isRestoring || appSessionRestore.pendingSnapshot === null ) {
            hasRestoredRef.current = false;

            return;
        }

        if ( hasRestoredRef.current ) {
            return;
        }

        const value = ( appSessionRestore.pendingSnapshot as unknown as Record<string, unknown> )[key] as TValue | undefined;
        if ( value === undefined ) {
            return;
        }

        if ( appSessionRestore.restoredKeys.includes( key ) ) {
            return;
        }

        const context: RestoreContext = {
            "snapshot":    appSessionRestore.pendingSnapshot,
            "isRestoring": appSessionRestore.isRestoring,
        };

        void restoreRef.current( value, context );
        hasRestoredRef.current = true;
        sessionRestoreService.markRestored( key );
    }, [ appSessionRestore, key ] );
}
